import { SimpleFaker } from '@faker-js/faker';
import { faker } from '@faker-js/faker/locale/en';
import { makeData } from 'mocks/helpers';
import type {
  AvailableCertDto,
  ComplianceDocGeneratorDetails,
  ComplianceDocTemplateDto,
  ComplianceDocTemplateFieldDto,
  ComplianceDocumentDto,
  ComplianceDocumentListItemDto,
  ComplianceGeneratorStyleDto,
  ComplianceWarningDetail,
  DocumentStatus,
} from 'features/compliance/types';

/**
 * Deterministic MirageJS fixtures for the Compliance Document Generator (CDG).
 * Seeded with faker so the data is stable across runs (safe for snapshot tests).
 * Imported by `mocks/api/handlers/compliance.ts`.
 */

/** Fixed reference date so faker-generated timestamps stay deterministic. */
const REF_DATE = '2026-06-01T00:00:00.000Z';

// Dedicated instance so seeding for GUIDs never perturbs the module's global `faker` sequence.
const guidFaker = new SimpleFaker();

/** Deterministic GUID for a given seed. */
const guid = (seed: number): string => {
  guidFaker.seed(seed);
  return guidFaker.string.uuid();
};

const STATUSES: DocumentStatus[] = ['Generated', 'Signed', 'ExpiringSoon', 'Expired', 'Archived'];

const TEMPLATE_NAMES: Record<number, string> = {
  1: 'REACH Declaration',
  2: 'CPSIA Certificate',
  3: 'OEKO-TEX Statement',
};

/** Template category per templateId — drives the list `Type` column and `templateType` filter. */
const TEMPLATE_TYPES: Record<number, string> = {
  1: 'Regulatory',
  2: 'Regulatory',
  3: 'Certification',
};

// ---------------------------------------------------------------------------
// Documents — 25 rows spanning every DocumentStatus value
// ---------------------------------------------------------------------------

const newComplianceDocument = (i: number): ComplianceDocumentDto => {
  faker.seed(1000 + i);
  const templateId = 1 + (i % 3);
  const styleId = 5000 + i;
  const certFileCount = 1 + (i % 3);
  return {
    id: faker.string.uuid(),
    orgId: 1,
    templateId,
    templateEditCount: faker.number.int({ min: 0, max: 12 }),
    styleId,
    complianceId: 9000 + (i % 7),
    fileName: `${faker.commerce.productName()} compliance.pdf`,
    status: STATUSES[i % STATUSES.length],
    dataSnapshotHash: faker.string.hexadecimal({ length: 64, prefix: '', casing: 'lower' }),
    blobPath: `compliance/${faker.string.uuid()}.pdf`,
    notes: i % 4 === 0 ? faker.lorem.sentence() : null,
    certFiles: makeData(certFileCount, j => ({
      complianceCertificateFileId: guid(2000 + i * 10 + j),
      position: j,
      fileHash:
        j % 2 === 0 ? faker.string.hexadecimal({ length: 64, prefix: '', casing: 'lower' }) : null,
    })),
    createdBy: 100 + (i % 5),
    createdAt: faker.date.recent({ days: 90, refDate: REF_DATE }).toISOString(),
    lastUpdated: faker.date.recent({ days: 10, refDate: REF_DATE }).toISOString(),
  };
};

export const complianceDocuments: ComplianceDocumentDto[] = makeData(25, newComplianceDocument);

export const complianceDocumentListItems: ComplianceDocumentListItemDto[] = complianceDocuments.map(
  (d, i) => ({
    id: d.id,
    fileName: d.fileName,
    status: d.status,
    templateId: d.templateId,
    templateName: TEMPLATE_NAMES[d.templateId],
    templateType: TEMPLATE_TYPES[d.templateId],
    styleId: d.styleId,
    styleName: `Style ${d.styleId}`,
    styleUserDefinedId: `ST-${1000 + i}`,
    complianceId: d.complianceId,
    certCount: d.certFiles.length,
    createdBy: d.createdBy,
    createdByName: `User ${d.createdBy}`,
    createdAt: d.createdAt,
    lastUpdated: d.lastUpdated,
  })
);

// ---------------------------------------------------------------------------
// Available certs — covers Style-only / Style+Item / expired / inaccessible /
// pending / multi-file (1:N) variants (AC of DL-59571)
// ---------------------------------------------------------------------------

export const availableCerts: AvailableCertDto[] = [
  // Style source, accessible, single file
  {
    complianceId: 9001,
    source: 'Style',
    sourceId: 5001,
    sourceName: 'Summer Tee',
    sourceCategory: null,
    isAccessible: true,
    isDisabled: false,
    disabledReason: null,
    files: [{ certificateFileId: guid(3001), fileName: 'reach-declaration.pdf', position: 0 }],
    certNumber: 'RC-1001',
    expiryDate: '2027-01-15',
    certificateTypeName: 'REACH',
    certState: 1,
  },
  // Item source, accessible, MULTI-file (1:N)
  {
    complianceId: 9002,
    source: 'Item',
    sourceId: 7002,
    sourceName: 'Organic Cotton 180gsm',
    sourceCategory: 'Fabric, Yarn',
    isAccessible: true,
    isDisabled: false,
    disabledReason: null,
    files: [
      { certificateFileId: guid(3002), fileName: 'oekotex-page1.pdf', position: 0 },
      { certificateFileId: guid(3003), fileName: 'oekotex-page2.pdf', position: 1 },
      { certificateFileId: guid(3004), fileName: 'oekotex-annex.pdf', position: 2 },
    ],
    certNumber: 'OT-2002',
    expiryDate: '2026-09-30',
    certificateTypeName: 'OEKO-TEX',
    certState: 1,
  },
  // Expired → disabled
  {
    complianceId: 9003,
    source: 'Style',
    sourceId: 5003,
    sourceName: 'Winter Coat',
    sourceCategory: null,
    isAccessible: true,
    isDisabled: true,
    disabledReason: 'Expired',
    files: [{ certificateFileId: guid(3005), fileName: 'cpsia-expired.pdf', position: 0 }],
    certNumber: 'CP-3003',
    expiryDate: '2024-02-01',
    certificateTypeName: 'CPSIA',
    certState: 1,
  },
  // Inaccessible → disabled
  {
    complianceId: 9004,
    source: 'Item',
    sourceId: 7004,
    sourceName: 'Recycled Polyester',
    sourceCategory: 'Fabric',
    isAccessible: false,
    isDisabled: true,
    disabledReason: 'Inaccessible',
    files: [{ certificateFileId: guid(3006), fileName: 'grs-certificate.pdf', position: 0 }],
    certNumber: 'GR-4004',
    expiryDate: '2027-06-01',
    certificateTypeName: 'GRS',
    certState: 1,
  },
  // Pending approval → disabled
  {
    complianceId: 9005,
    source: 'Style',
    sourceId: 5005,
    sourceName: 'Denim Jacket',
    sourceCategory: null,
    isAccessible: true,
    isDisabled: true,
    disabledReason: 'Pending approval',
    files: [{ certificateFileId: guid(3007), fileName: 'reach-pending.pdf', position: 0 }],
    certNumber: 'PD-5005',
    expiryDate: '2028-01-01',
    certificateTypeName: 'REACH',
    certState: 2,
  },
];

// ---------------------------------------------------------------------------
// Reused lookups
// ---------------------------------------------------------------------------

export const complianceDocTemplates: ComplianceDocTemplateDto[] = [
  { id: 1, name: 'REACH Declaration', version: 3, type: TEMPLATE_TYPES[1] },
  { id: 2, name: 'CPSIA Certificate', version: 1, type: TEMPLATE_TYPES[2] },
  { id: 3, name: 'OEKO-TEX Statement', version: 2, type: TEMPLATE_TYPES[3] },
];

export const complianceDocTemplateFields: ComplianceDocTemplateFieldDto[] = [
  { id: 11, fieldId: 1, label: 'Substance', resultSet: null },
  { id: 12, fieldId: 2, label: 'Limit', resultSet: 'ppm' },
  { id: 13, fieldId: 3, label: 'Result', resultSet: 'pass/fail' },
];

export const complianceGeneratorStyles: ComplianceGeneratorStyleDto[] = [
  { id: 5001, name: 'Summer Tee', userDefinedId: 'ST-1001' },
  { id: 5002, name: 'Organic Hoodie', userDefinedId: 'ST-1002' },
  { id: 5003, name: 'Winter Coat', userDefinedId: 'ST-1003' },
];

// ---------------------------------------------------------------------------
// Preview (structural mirror; includes a [MISSING:] token + N/A row)
// ---------------------------------------------------------------------------

export const complianceGeneratorPreview: ComplianceDocGeneratorDetails = {
  sections: [
    {
      kind: 'section',
      label: 'Product Information',
      children: [
        { kind: 'fieldRow', label: 'Style number', value: 'ST-1001' },
        { kind: 'fieldRow', label: 'Composition', value: '100% Organic Cotton' },
        {
          kind: 'fieldRow',
          label: 'Supplier declaration',
          value: '[MISSING:supplierDeclaration]',
          isMissing: true,
        },
      ],
    },
    {
      kind: 'section',
      label: 'Test Results',
      children: [
        { kind: 'fieldRow', label: 'REACH', value: 'Pass' },
        { kind: 'naRow', label: 'CPSIA', value: 'N/A' },
      ],
    },
    { kind: 'paragraph', value: 'Generated for compliance documentation purposes.' },
  ],
};

// ---------------------------------------------------------------------------
// Download — fixture body, sample warnings, and branch triggers
// ---------------------------------------------------------------------------

/**
 * styleId values that drive the `/download` handler down a specific branch, so
 * the download hook (and the smoke test) can exercise each outcome deterministically.
 */
export const DOWNLOAD_TRIGGERS = {
  /** 200 + `X-Compliance-Warnings` header (partial success / skip-and-warn). */
  partialWarnings: 999001,
  /** 409 `merge_failed` (unrecoverable). */
  unrecoverable: 999002,
  /** 400 `merge_payload_too_large` (cap by payload size). */
  payloadTooLarge: 999003,
} as const;

/** GUID that DELETE treats as forbidden (→ 403 `action_not_allowed`). */
export const FORBIDDEN_DELETE_DOC_ID = '00000000-0000-0000-0000-0000000000ff';

/** Stable GUID returned in the `X-ComplianceDocumentId` header from `/download`. */
export const DOWNLOAD_DOCUMENT_ID = '11111111-1111-1111-1111-111111111111';

/** Sample skip-and-warn warnings for the partial-success `/download` branch. */
export const sampleDownloadWarnings: ComplianceWarningDetail[] = [
  { type: 'merge_failed_missing_blob', field: 'certificateFileId', value: guid(3005) },
  { type: 'merge_failed_password_protected_pdf', field: 'certificateFileId', value: guid(3006) },
];

/** A minimal but structurally valid single-page PDF, used as the mock download body. */
const MINIMAL_PDF = [
  '%PDF-1.1',
  '1 0 obj<</Type/Catalog/Pages 2 0 R>>endobj',
  '2 0 obj<</Type/Pages/Kids[3 0 R]/Count 1>>endobj',
  '3 0 obj<</Type/Page/Parent 2 0 R/MediaBox[0 0 200 200]>>endobj',
  'trailer<</Root 1 0 R>>',
  '%%EOF',
].join('\n');

/**
 * The mock `/download` response body, as a string of the PDF bytes.
 *
 * NOTE: MirageJS/Pretender cannot round-trip a true binary `ArrayBuffer` body
 * through jsdom's XHR — MirageJS `JSON.stringify`s a non-string body (an
 * `ArrayBuffer` serialises to `"{}"`), and Pretender's fake XHR ignores
 * `responseType: 'arraybuffer'` in jsdom (it returns `responseText`).
 * Returning the PDF as a string (with `Content-Type: application/pdf`)
 * round-trips faithfully in both tests (as a string) and the dev browser; the
 * download hook wraps whatever it receives in a Blob. Against the real backend
 * the response is true binary — the contract is unchanged.
 */
export const mockPdfBody = (): string => MINIMAL_PDF;
